/**
 * Canvas Interaction Hook
 * Handles layer selection, dragging, tool modes, and keyboard shortcuts
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { nanoid } from 'nanoid';
import { useTemplateStore } from '@/stores/designStore';
import { useToolbarStore } from '@/stores/toolbarStore';
import { useEditorStore } from '@/stores/editorStore';
import { useHistoryStore } from '@/stores/historyStore';
import type { Layer, TextLayer, ShapeLayer, ImageLayer } from '@/types/layers';
import type { AdFormat } from '@/types/formats';
import type { ToolId } from '@/types/tools';
import { createAbsolutePosition } from '@/types/position';
import { TOOLS } from '@/components/toolbar';
import { resolvePosition, type ContainerDimensions } from '@/lib/position/resolver';
import { calculateSmartGuides, type Guide } from '@/components/canvas/SmartGuides';
import { type HandlePosition, type ResizeBounds, calculateNewBounds } from '@/lib/resize';
import { solid } from '@/types/brush';

interface UseCanvasInteractionOptions {
  format: AdFormat;
  enabled?: boolean;
}

interface DragState {
  isDragging: boolean;
  layerId: string | null;
  initialPosition: { x: number; y: number } | null;
  grabOffset: { x: number; y: number }; // Offset from layer origin to grab point
}

interface ShapeDrawState {
  isDrawing: boolean;
  startX: number;
  startY: number;
  currentX: number;
  currentY: number;
  layerId: string | null;
}

interface PanState {
  isPanning: boolean;
  startX: number;
  startY: number;
  initialPanOffset: { x: number; y: number };
}

interface ResizeState {
  isResizing: boolean;
  layerId: string | null;
  handlePosition: HandlePosition | null;
  initialBounds: ResizeBounds | null;
  initialMousePos: { x: number; y: number } | null;
  aspectRatio: number;
}

// Hidden file input for image uploads
let fileInputElement: HTMLInputElement | null = null;

function getOrCreateFileInput(onSelect: (file: File) => void): HTMLInputElement {
  if (!fileInputElement) {
    fileInputElement = document.createElement('input');
    fileInputElement.type = 'file';
    fileInputElement.accept = 'image/*';
    fileInputElement.style.display = 'none';
    document.body.appendChild(fileInputElement);
  }

  fileInputElement.onchange = (e) => {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (file) {
      onSelect(file);
    }
    // Reset the input so the same file can be selected again
    fileInputElement!.value = '';
  };

  return fileInputElement;
}

export function useCanvasInteraction({ format, enabled = true }: UseCanvasInteractionOptions) {
  const canvasRef = useRef<HTMLDivElement>(null);
  const [dragState, setDragState] = useState<DragState>({
    isDragging: false,
    layerId: null,
    initialPosition: null,
    grabOffset: { x: 0, y: 0 },
  });

  const [shapeDrawState, setShapeDrawState] = useState<ShapeDrawState>({
    isDrawing: false,
    startX: 0,
    startY: 0,
    currentX: 0,
    currentY: 0,
    layerId: null,
  });

  const [panState, setPanState] = useState<PanState>({
    isPanning: false,
    startX: 0,
    startY: 0,
    initialPanOffset: { x: 0, y: 0 },
  });

  const [resizeState, setResizeState] = useState<ResizeState>({
    isResizing: false,
    layerId: null,
    handlePosition: null,
    initialBounds: null,
    initialMousePos: null,
    aspectRatio: 1,
  });

  // Smart guides during drag
  const [guides, setGuides] = useState<Guide[]>([]);

  // Color picker state
  const [colorPickerState, setColorPickerState] = useState<{
    previewColor: string | null;
    pickedColor: string | null;
    showPickedNotification: boolean;
  }>({
    previewColor: null,
    pickedColor: null,
    showPickedNotification: false,
  });

  // Track pending image position
  const pendingImagePosition = useRef<{ x: number; y: number } | null>(null);

  // Track space key for temporary hand tool
  const [isSpaceHeld, setIsSpaceHeld] = useState(false);

  const {
    currentTemplate,
    selectedLayerIds,
    selectLayer,
    selectLayers,
    clearSelection,
    setHoveredLayer,
    updateLayer,
    deleteLayer,
    duplicateLayer,
    groupSelectedLayers,
    ungroupLayer,
    copySelectedLayers,
    cutSelectedLayers,
    pasteLayers,
    getLayerById,
    moveLayerUp,
    moveLayerDown,
    addLayer,
  } = useTemplateStore();

  const {
    undo,
    redo,
    startTransaction,
    commitTransaction,
  } = useHistoryStore();

  const {
    activeTool,
    setActiveTool,
    textDefaults,
    shapeDefaults,
    lineDefaults,
    updateShapeDefaults,
  } = useToolbarStore();

  const { panOffset, setPanOffset } = useEditorStore();

  // Get effective tool (space overrides to hand)
  const effectiveTool: ToolId = isSpaceHeld ? 'hand' : activeTool;

  // Get cursor for current tool (use grabbing when actively panning)
  const toolConfig = TOOLS.find(t => t.id === effectiveTool);
  const toolCursor = panState.isPanning ? 'grabbing' : (toolConfig?.cursor || 'default');

  // Convert screen coordinates to canvas coordinates
  const screenToCanvas = useCallback((clientX: number, clientY: number): { x: number; y: number } | null => {
    const canvas = document.getElementById('ad-canvas');
    if (!canvas) return null;

    const rect = canvas.getBoundingClientRect();
    const zoom = rect.width / format.width;

    return {
      x: (clientX - rect.left) / zoom,
      y: (clientY - rect.top) / zoom,
    };
  }, [format]);

  // Create text layer at position
  const createTextAtPosition = useCallback((x: number, y: number) => {
    const layer: TextLayer = {
      id: nanoid(),
      type: 'text',
      name: 'New Text',
      position: createAbsolutePosition(x, y),
      visible: true,
      locked: false,
      opacity: 1,
      content: {
        text: 'New Text',
        fontSize: textDefaults.fontSize,
        fontFamily: textDefaults.fontFamily,
        fontWeight: textDefaults.fontWeight,
        color: textDefaults.color,
        textAlign: textDefaults.textAlign,
      },
    };

    addLayer(layer);
    selectLayer(layer.id);
    setActiveTool('select');
  }, [textDefaults, addLayer, selectLayer, setActiveTool]);

  // Create shape layer using shape type from toolbar defaults
  const createShapeLayer = useCallback((
    startX: number,
    startY: number,
    width: number,
    height: number,
    constrained: boolean
  ) => {
    const shapeType = shapeDefaults.shapeType;

    // If constrained (shift held), make it a square/circle
    let finalWidth = width;
    let finalHeight = height;
    if (constrained) {
      const size = Math.max(Math.abs(width), Math.abs(height));
      finalWidth = width < 0 ? -size : size;
      finalHeight = height < 0 ? -size : size;
    }

    // Normalize negative dimensions
    let finalX = startX;
    let finalY = startY;
    if (finalWidth < 0) {
      finalX = startX + finalWidth;
      finalWidth = Math.abs(finalWidth);
    }
    if (finalHeight < 0) {
      finalY = startY + finalHeight;
      finalHeight = Math.abs(finalHeight);
    }

    // Minimum size
    finalWidth = Math.max(finalWidth, 10);
    finalHeight = Math.max(finalHeight, 10);

    // Name based on shape type
    const shapeNames: Record<string, string> = {
      rectangle: 'Rectangle',
      circle: 'Circle',
      ellipse: 'Ellipse',
      triangle: 'Triangle',
      pentagon: 'Pentagon',
      hexagon: 'Hexagon',
      star: 'Star',
    };

    const layer: ShapeLayer = {
      id: nanoid(),
      type: 'shape',
      name: shapeNames[shapeType] || 'Shape',
      position: createAbsolutePosition(finalX, finalY),
      width: finalWidth,
      height: finalHeight,
      visible: true,
      locked: false,
      opacity: 1,
      content: {
        shapeType,
        fill: shapeDefaults.fill,
        stroke: shapeDefaults.stroke ?? undefined,
        strokeWidth: shapeDefaults.strokeWidth || undefined,
        borderRadius: shapeType === 'rectangle' ? shapeDefaults.borderRadius : undefined,
      },
    };

    return layer;
  }, [shapeDefaults]);

  // Create line layer
  const createLineLayer = useCallback((
    startX: number,
    startY: number,
    endX: number,
    endY: number,
    constrained: boolean
  ) => {
    // If constrained, snap to 45-degree angles
    let finalEndX = endX;
    let finalEndY = endY;
    if (constrained) {
      const dx = endX - startX;
      const dy = endY - startY;
      const angle = Math.atan2(dy, dx);
      const snappedAngle = Math.round(angle / (Math.PI / 4)) * (Math.PI / 4);
      const length = Math.sqrt(dx * dx + dy * dy);
      finalEndX = startX + Math.cos(snappedAngle) * length;
      finalEndY = startY + Math.sin(snappedAngle) * length;
    }

    // Calculate width/height from line endpoints
    const width = Math.abs(finalEndX - startX) || 1;
    const height = Math.abs(finalEndY - startY) || 1;

    const layer: ShapeLayer = {
      id: nanoid(),
      type: 'shape',
      name: 'Line',
      position: createAbsolutePosition(Math.min(startX, finalEndX), Math.min(startY, finalEndY)),
      width,
      height,
      visible: true,
      locked: false,
      opacity: 1,
      content: {
        shapeType: 'line',
        stroke: lineDefaults.stroke,
        strokeWidth: lineDefaults.strokeWidth,
      },
    };

    return layer;
  }, [lineDefaults]);

  // Handle image upload
  const handleImageUpload = useCallback((file: File, x: number, y: number) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const src = e.target?.result as string;
      if (!src) return;

      // Create image to get dimensions
      const img = new Image();
      img.onload = () => {
        // Scale down if too large
        let width = img.width;
        let height = img.height;
        const maxSize = 400;
        if (width > maxSize || height > maxSize) {
          const scale = maxSize / Math.max(width, height);
          width = width * scale;
          height = height * scale;
        }

        const layer: ImageLayer = {
          id: nanoid(),
          type: 'image',
          name: file.name.split('.')[0] || 'Image',
          position: createAbsolutePosition(x - width / 2, y - height / 2),
          width,
          height,
          visible: true,
          locked: false,
          opacity: 1,
          content: {
            src,
            objectFit: 'cover',
          },
        };

        addLayer(layer);
        selectLayer(layer.id);
        setActiveTool('select');
      };
      img.src = src;
    };
    reader.readAsDataURL(file);
  }, [addLayer, selectLayer, setActiveTool]);

  // Get color from DOM element at point (fallback for non-EyeDropper browsers)
  const getColorFromElement = useCallback((element: Element | null): string | null => {
    if (!element) return null;

    // Walk up the DOM tree looking for a color
    let current: Element | null = element;
    while (current && current !== document.body) {
      const style = window.getComputedStyle(current);

      // Check background color
      const bgColor = style.backgroundColor;
      if (bgColor && bgColor !== 'rgba(0, 0, 0, 0)' && bgColor !== 'transparent') {
        return rgbToHex(bgColor);
      }

      // Check SVG fill
      const fill = style.fill;
      if (fill && fill !== 'none' && fill !== 'transparent') {
        // Fill might be "rgb(...)" or a color name
        if (fill.startsWith('rgb')) {
          return rgbToHex(fill);
        } else if (fill.startsWith('#')) {
          return fill;
        }
      }

      // Check text color (for text elements)
      if (current.textContent && current.textContent.trim()) {
        const color = style.color;
        if (color) {
          return rgbToHex(color);
        }
      }

      current = current.parentElement;
    }

    return null;
  }, []);

  // Convert rgb/rgba string to hex
  const rgbToHex = (rgb: string): string => {
    const match = rgb.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    if (!match) return rgb;

    const r = parseInt(match[1], 10);
    const g = parseInt(match[2], 10);
    const b = parseInt(match[3], 10);

    return '#' + [r, g, b].map(x => x.toString(16).padStart(2, '0')).join('').toUpperCase();
  };

  // Show picked color notification
  const showColorPicked = useCallback((color: string) => {
    // Copy to clipboard
    navigator.clipboard.writeText(color);

    // Update shape defaults with sampled color
    updateShapeDefaults({ fill: solid(color) });

    // Show picked notification
    setColorPickerState({
      previewColor: null,
      pickedColor: color,
      showPickedNotification: true,
    });

    // Hide notification after 2 seconds
    setTimeout(() => {
      setColorPickerState(prev => ({
        ...prev,
        showPickedNotification: false,
      }));
    }, 2000);
  }, [updateShapeDefaults]);

  // Use native EyeDropper API if available
  const useNativeEyeDropper = useCallback(async (): Promise<boolean> => {
    // Check if EyeDropper API is available
    if (!('EyeDropper' in window)) {
      return false;
    }

    try {
      // @ts-expect-error EyeDropper is not in TypeScript's lib yet
      const eyeDropper = new window.EyeDropper();
      const result = await eyeDropper.open();
      const color = result.sRGBHex.toUpperCase();
      showColorPicked(color);
      return true;
    } catch {
      // User cancelled or error
      return false;
    }
  }, [showColorPicked]);

  // Handle color picker activation
  const activateColorPicker = useCallback(async () => {
    // Try native EyeDropper first
    const usedNative = await useNativeEyeDropper();
    if (usedNative) {
      // Switch back to select tool after picking
      setActiveTool('select');
    }
    // If native not available, we rely on click handler below
  }, [useNativeEyeDropper, setActiveTool]);

  // Handle color picker click (fallback when EyeDropper not available)
  const handleColorPickClick = useCallback((event: React.MouseEvent) => {
    const element = document.elementFromPoint(event.clientX, event.clientY);
    const color = getColorFromElement(element);

    if (color) {
      showColorPicked(color);
    }
  }, [getColorFromElement, showColorPicked]);

  // Get preview color from element under cursor
  const getPreviewColorFromPoint = useCallback((x: number, y: number): string | null => {
    const element = document.elementFromPoint(x, y);
    return getColorFromElement(element);
  }, [getColorFromElement]);

  // Handle layer click
  const handleLayerClick = useCallback(
    (layerId: string, event: React.MouseEvent) => {
      if (!enabled) return;

      const layer = getLayerById(layerId);
      if (!layer || layer.locked) return;

      // Handle color picker tool - use DOM-based sampling
      if (effectiveTool === 'colorPicker') {
        handleColorPickClick(event);
        setActiveTool('select'); // Switch back to select after picking
        return;
      }

      // For select tool, handle selection
      if (effectiveTool === 'select') {
        if (event.shiftKey) {
          // Add to selection with shift
          if (selectedLayerIds.includes(layerId)) {
            selectLayers(selectedLayerIds.filter((id) => id !== layerId));
          } else {
            selectLayers([...selectedLayerIds, layerId]);
          }
        } else if (event.metaKey || event.ctrlKey) {
          // Toggle selection with cmd/ctrl
          if (selectedLayerIds.includes(layerId)) {
            selectLayers(selectedLayerIds.filter((id) => id !== layerId));
          } else {
            selectLayers([...selectedLayerIds, layerId]);
          }
        } else {
          // Single selection
          selectLayer(layerId);
        }
      }
    },
    [enabled, selectedLayerIds, selectLayer, selectLayers, getLayerById, effectiveTool, handleColorPickClick, setActiveTool]
  );

  // Handle canvas background click (deselect or create)
  const handleCanvasClick = useCallback(
    (event: React.MouseEvent) => {
      if (!enabled) return;

      // Check if click was on the canvas background, not a layer
      const target = event.target as HTMLElement;
      if (target.dataset.layerId) return;

      const pos = screenToCanvas(event.clientX, event.clientY);
      if (!pos) return;

      // Handle tool-specific actions
      switch (effectiveTool) {
        case 'select':
          clearSelection();
          break;

        case 'text':
          createTextAtPosition(pos.x, pos.y);
          break;

        case 'image': {
          // Store position for when image is selected
          pendingImagePosition.current = pos;
          const input = getOrCreateFileInput((file) => {
            if (pendingImagePosition.current) {
              handleImageUpload(file, pendingImagePosition.current.x, pendingImagePosition.current.y);
            }
          });
          input.click();
          break;
        }

        case 'colorPicker':
          // Pick color from clicked point
          handleColorPickClick(event);
          setActiveTool('select');
          break;

        case 'hand':
          // Pan is handled by drag, not click
          break;

        // Shape tools start drawing on mousedown, not click
        default:
          break;
      }
    },
    [enabled, clearSelection, effectiveTool, screenToCanvas, createTextAtPosition, handleImageUpload, handleColorPickClick, setActiveTool]
  );

  // Handle hover
  const handleLayerHover = useCallback(
    (layerId: string | null) => {
      if (!enabled) return;
      setHoveredLayer(layerId);
    },
    [enabled, setHoveredLayer]
  );

  // Track mouse position for color picker preview
  useEffect(() => {
    if (!enabled || effectiveTool !== 'colorPicker') {
      // Clear preview when not using color picker
      if (colorPickerState.previewColor !== null) {
        setColorPickerState(prev => ({ ...prev, previewColor: null }));
      }
      return;
    }

    const handleMouseMove = (event: MouseEvent) => {
      const color = getPreviewColorFromPoint(event.clientX, event.clientY);
      setColorPickerState(prev => ({
        ...prev,
        previewColor: color,
      }));
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [enabled, effectiveTool, getPreviewColorFromPoint, colorPickerState.previewColor]);

  // Handle drag start (for select tool)
  const handleMouseDown = useCallback(
    (event: React.MouseEvent, layerId: string) => {
      if (!enabled) return;

      // Only handle for select tool (or hand tool for panning)
      if (effectiveTool !== 'select' && effectiveTool !== 'hand') return;

      const layer = getLayerById(layerId);
      if (!layer || layer.locked) return;

      // Get click position in canvas coordinates
      const clickPos = screenToCanvas(event.clientX, event.clientY);
      if (!clickPos) return;

      // Get initial position - resolve if not absolute
      let initialPosition = { x: 0, y: 0 };
      if (layer.position.mode === 'absolute') {
        initialPosition = { x: layer.position.x, y: layer.position.y };
      } else {
        // Resolve the position to get actual rendered coordinates
        const container: ContainerDimensions = { width: format.width, height: format.height };
        const layerDims = {
          width: typeof layer.width === 'number' ? layer.width : 100,
          height: typeof layer.height === 'number' ? layer.height : 100,
        };
        const resolved = resolvePosition(layer.position, container, layerDims);
        initialPosition = { x: resolved.x, y: resolved.y };
      }

      // Calculate grab offset (where user clicked relative to layer origin)
      const grabOffset = {
        x: clickPos.x - initialPosition.x,
        y: clickPos.y - initialPosition.y,
      };

      // Start a transaction for the drag operation
      startTransaction('Move Layer');

      setDragState({
        isDragging: true,
        layerId,
        initialPosition,
        grabOffset,
      });
    },
    [enabled, getLayerById, effectiveTool, format, screenToCanvas, startTransaction]
  );

  // Handle shape/line drawing and panning mousedown on canvas
  const handleCanvasMouseDown = useCallback(
    (event: React.MouseEvent) => {
      if (!enabled) return;

      // Handle panning with hand tool
      if (effectiveTool === 'hand') {
        event.preventDefault();
        setPanState({
          isPanning: true,
          startX: event.clientX,
          startY: event.clientY,
          initialPanOffset: { ...panOffset },
        });
        return;
      }

      // Only for shape/line tools
      if (!['shape', 'line'].includes(effectiveTool)) return;

      // Don't start drawing if clicking on a layer
      const target = event.target as HTMLElement;
      if (target.dataset.layerId) return;

      const pos = screenToCanvas(event.clientX, event.clientY);
      if (!pos) return;

      setShapeDrawState({
        isDrawing: true,
        startX: pos.x,
        startY: pos.y,
        currentX: pos.x,
        currentY: pos.y,
        layerId: null,
      });
    },
    [enabled, effectiveTool, screenToCanvas, panOffset]
  );

  // Handle panning from anywhere in the grid area (container)
  const handleContainerMouseDown = useCallback(
    (event: React.MouseEvent) => {
      if (!enabled) return;

      // Only pan with hand tool
      if (effectiveTool !== 'hand') return;

      // Don't start pan if clicking on the canvas itself (let canvas handle it)
      const canvas = document.getElementById('ad-canvas');
      if (canvas && canvas.contains(event.target as Node)) return;

      event.preventDefault();
      setPanState({
        isPanning: true,
        startX: event.clientX,
        startY: event.clientY,
        initialPanOffset: { ...panOffset },
      });
    },
    [enabled, effectiveTool, panOffset]
  );

  // Handle drag move with smart guides
  useEffect(() => {
    if (!dragState.isDragging || !dragState.layerId) return;

    const handleMouseMove = (event: MouseEvent) => {
      // Get current mouse position in canvas coordinates
      const mousePos = screenToCanvas(event.clientX, event.clientY);
      if (!mousePos) return;

      if (dragState.layerId) {
        // New position = mouse position minus grab offset
        let newX = mousePos.x - dragState.grabOffset.x;
        let newY = mousePos.y - dragState.grabOffset.y;

        // Get layer dimensions
        const layer = getLayerById(dragState.layerId);
        const layerWidth = typeof layer?.width === 'number' ? layer.width : 100;
        const layerHeight = typeof layer?.height === 'number' ? layer.height : 100;

        // Get other layers' bounds for smart guides
        const otherLayers = currentTemplate?.layers
          .filter(l => l.id !== dragState.layerId && l.visible)
          .map(l => {
            const container: ContainerDimensions = { width: format.width, height: format.height };
            const dims = {
              width: typeof l.width === 'number' ? l.width : 100,
              height: typeof l.height === 'number' ? l.height : 100,
            };
            const resolved = resolvePosition(l.position, container, dims);
            return {
              id: l.id,
              x: resolved.x,
              y: resolved.y,
              width: resolved.width ?? dims.width,
              height: resolved.height ?? dims.height,
            };
          }) ?? [];

        // Calculate smart guides
        const { guides: newGuides, snappedX, snappedY } = calculateSmartGuides(
          { id: dragState.layerId, x: newX, y: newY, width: layerWidth, height: layerHeight },
          otherLayers,
          format.width,
          format.height
        );

        // Apply snap if within threshold
        if (snappedX !== undefined) newX = snappedX;
        if (snappedY !== undefined) newY = snappedY;

        setGuides(newGuides);

        updateLayer(dragState.layerId, {
          position: {
            mode: 'absolute',
            x: newX,
            y: newY,
          },
        });
      }
    };

    const handleMouseUp = () => {
      // Commit the transaction to record all drag movements as a single undo step
      commitTransaction();

      setDragState({
        isDragging: false,
        layerId: null,
        initialPosition: null,
        grabOffset: { x: 0, y: 0 },
      });
      setGuides([]); // Clear guides when done dragging
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragState, updateLayer, format, currentTemplate, getLayerById, screenToCanvas, commitTransaction]);

  // Handle canvas panning
  useEffect(() => {
    if (!panState.isPanning) return;

    const handleMouseMove = (event: MouseEvent) => {
      const deltaX = event.clientX - panState.startX;
      const deltaY = event.clientY - panState.startY;

      setPanOffset({
        x: panState.initialPanOffset.x + deltaX,
        y: panState.initialPanOffset.y + deltaY,
      });
    };

    const handleMouseUp = () => {
      setPanState({
        isPanning: false,
        startX: 0,
        startY: 0,
        initialPanOffset: { x: 0, y: 0 },
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [panState, setPanOffset]);

  // Handle shape drawing
  useEffect(() => {
    if (!shapeDrawState.isDrawing) return;

    const handleMouseMove = (event: MouseEvent) => {
      const pos = screenToCanvas(event.clientX, event.clientY);
      if (!pos) return;

      setShapeDrawState(prev => ({
        ...prev,
        currentX: pos.x,
        currentY: pos.y,
      }));
    };

    const handleMouseUp = (event: MouseEvent) => {
      const pos = screenToCanvas(event.clientX, event.clientY);
      if (!pos) {
        setShapeDrawState({
          isDrawing: false,
          startX: 0,
          startY: 0,
          currentX: 0,
          currentY: 0,
          layerId: null,
        });
        return;
      }

      const width = pos.x - shapeDrawState.startX;
      const height = pos.y - shapeDrawState.startY;
      const constrained = event.shiftKey;

      let layer: Layer | null = null;

      if (effectiveTool === 'shape') {
        layer = createShapeLayer(shapeDrawState.startX, shapeDrawState.startY, width, height, constrained);
      } else if (effectiveTool === 'line') {
        layer = createLineLayer(shapeDrawState.startX, shapeDrawState.startY, pos.x, pos.y, constrained);
      }

      if (layer) {
        addLayer(layer);
        selectLayer(layer.id);
      }

      setShapeDrawState({
        isDrawing: false,
        startX: 0,
        startY: 0,
        currentX: 0,
        currentY: 0,
        layerId: null,
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [shapeDrawState, screenToCanvas, effectiveTool, createShapeLayer, createLineLayer, addLayer, selectLayer]);

  // Handle resize start
  const handleResizeStart = useCallback(
    (layerId: string, handle: HandlePosition, event: React.MouseEvent) => {
      if (!enabled) return;

      const layer = getLayerById(layerId);
      if (!layer || layer.locked) return;

      // Get layer dimensions
      const layerWidth = typeof layer.width === 'number' ? layer.width : 100;
      const layerHeight = typeof layer.height === 'number' ? layer.height : 100;

      // Resolve position
      const container: ContainerDimensions = { width: format.width, height: format.height };
      const dims = { width: layerWidth, height: layerHeight };
      const resolved = resolvePosition(layer.position, container, dims);

      const initialBounds: ResizeBounds = {
        x: resolved.x,
        y: resolved.y,
        width: resolved.width ?? layerWidth,
        height: resolved.height ?? layerHeight,
      };

      const aspectRatio = initialBounds.width / initialBounds.height;

      // Start a transaction for the resize operation
      startTransaction('Resize Layer');

      setResizeState({
        isResizing: true,
        layerId,
        handlePosition: handle,
        initialBounds,
        initialMousePos: { x: event.clientX, y: event.clientY },
        aspectRatio,
      });
    },
    [enabled, getLayerById, format, startTransaction]
  );

  // Handle resize with mouse move
  useEffect(() => {
    if (!resizeState.isResizing || !resizeState.layerId || !resizeState.initialBounds || !resizeState.initialMousePos || !resizeState.handlePosition) {
      return;
    }

    const handleMouseMove = (event: MouseEvent) => {
      // Get zoom level from canvas
      const canvas = document.getElementById('ad-canvas');
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const zoom = rect.width / format.width;

      // Calculate delta in canvas coordinates
      const deltaX = (event.clientX - resizeState.initialMousePos!.x) / zoom;
      const deltaY = (event.clientY - resizeState.initialMousePos!.y) / zoom;

      // Check if aspect ratio should be constrained
      const constrainAspect = event.shiftKey || event.ctrlKey;

      // Calculate new bounds
      const newBounds = calculateNewBounds(
        resizeState.initialBounds!,
        resizeState.handlePosition!,
        deltaX,
        deltaY,
        constrainAspect,
        resizeState.aspectRatio
      );

      // Enforce minimum size
      const minSize = 10;
      if (newBounds.width < minSize) {
        newBounds.width = minSize;
        // Adjust position if needed
        if (resizeState.handlePosition === 'nw' || resizeState.handlePosition === 'sw' || resizeState.handlePosition === 'w') {
          newBounds.x = resizeState.initialBounds!.x + resizeState.initialBounds!.width - minSize;
        }
      }
      if (newBounds.height < minSize) {
        newBounds.height = minSize;
        // Adjust position if needed
        if (resizeState.handlePosition === 'nw' || resizeState.handlePosition === 'ne' || resizeState.handlePosition === 'n') {
          newBounds.y = resizeState.initialBounds!.y + resizeState.initialBounds!.height - minSize;
        }
      }

      // Update layer
      updateLayer(resizeState.layerId!, {
        position: {
          mode: 'absolute',
          x: newBounds.x,
          y: newBounds.y,
        },
        width: newBounds.width,
        height: newBounds.height,
      });
    };

    const handleMouseUp = () => {
      // Commit the transaction
      commitTransaction();

      setResizeState({
        isResizing: false,
        layerId: null,
        handlePosition: null,
        initialBounds: null,
        initialMousePos: null,
        aspectRatio: 1,
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizeState, format, updateLayer, commitTransaction]);

  // Keyboard shortcuts
  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      // Don't handle if typing in an input
      const target = event.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return;
      }

      const isMeta = event.metaKey || event.ctrlKey;

      // Handle space for temporary hand tool
      if (event.code === 'Space' && !event.repeat) {
        event.preventDefault();
        setIsSpaceHeld(true);
        return;
      }

      // Handle tool shortcuts (only when no modifiers)
      if (!isMeta && !event.altKey) {
        const key = event.key.toUpperCase();
        const tool = TOOLS.find(t => t.shortcut === key);
        if (tool) {
          event.preventDefault();
          setActiveTool(tool.id);
          return;
        }
      }

      switch (event.key) {
        case 'Delete':
        case 'Backspace':
          // Delete selected layers
          if (selectedLayerIds.length > 0) {
            event.preventDefault();
            selectedLayerIds.forEach((id) => deleteLayer(id));
          }
          break;

        case 'd':
          // Duplicate (Cmd+D)
          if (isMeta && selectedLayerIds.length > 0) {
            event.preventDefault();
            selectedLayerIds.forEach((id) => duplicateLayer(id));
          }
          break;

        case 'g':
          // Group (Cmd+G) / Ungroup (Cmd+Shift+G)
          if (isMeta) {
            event.preventDefault();
            if (event.shiftKey) {
              // Ungroup
              selectedLayerIds.forEach((id) => {
                const layer = getLayerById(id);
                if (layer?.type === 'group') {
                  ungroupLayer(id);
                }
              });
            } else {
              // Group
              if (selectedLayerIds.length >= 2) {
                groupSelectedLayers();
              }
            }
          }
          break;

        case 'c':
          // Copy (Cmd+C)
          if (isMeta && selectedLayerIds.length > 0) {
            event.preventDefault();
            copySelectedLayers();
          }
          break;

        case 'x':
          // Cut (Cmd+X)
          if (isMeta && selectedLayerIds.length > 0) {
            event.preventDefault();
            cutSelectedLayers();
          }
          break;

        case 'v':
          // Paste (Cmd+V) - note: V without cmd is the Select tool shortcut
          if (isMeta) {
            event.preventDefault();
            pasteLayers();
          }
          break;

        case 'z':
          // Undo (Cmd+Z) / Redo (Cmd+Shift+Z)
          if (isMeta) {
            event.preventDefault();
            if (event.shiftKey) {
              redo();
            } else {
              undo();
            }
          }
          break;

        case 'a':
          // Select all (Cmd+A)
          if (isMeta && currentTemplate) {
            event.preventDefault();
            selectLayers(currentTemplate.layers.map((l) => l.id));
          }
          break;

        case 'Escape':
          // Deselect all and return to select tool
          clearSelection();
          setActiveTool('select');
          break;

        case 'ArrowUp':
          // Move up in layer order (Cmd+Up) or nudge position
          if (selectedLayerIds.length === 1) {
            event.preventDefault();
            if (isMeta) {
              moveLayerUp(selectedLayerIds[0]);
            } else {
              const layer = getLayerById(selectedLayerIds[0]);
              if (layer?.position.mode === 'absolute') {
                const delta = event.shiftKey ? 10 : 1;
                updateLayer(selectedLayerIds[0], {
                  position: { mode: 'absolute', x: layer.position.x, y: layer.position.y - delta },
                });
              }
            }
          }
          break;

        case 'ArrowDown':
          // Move down in layer order (Cmd+Down) or nudge position
          if (selectedLayerIds.length === 1) {
            event.preventDefault();
            if (isMeta) {
              moveLayerDown(selectedLayerIds[0]);
            } else {
              const layer = getLayerById(selectedLayerIds[0]);
              if (layer?.position.mode === 'absolute') {
                const delta = event.shiftKey ? 10 : 1;
                updateLayer(selectedLayerIds[0], {
                  position: { mode: 'absolute', x: layer.position.x, y: layer.position.y + delta },
                });
              }
            }
          }
          break;

        case 'ArrowLeft':
          // Nudge position left
          if (selectedLayerIds.length === 1) {
            event.preventDefault();
            const layer = getLayerById(selectedLayerIds[0]);
            if (layer?.position.mode === 'absolute') {
              const delta = event.shiftKey ? 10 : 1;
              updateLayer(selectedLayerIds[0], {
                position: { mode: 'absolute', x: layer.position.x - delta, y: layer.position.y },
              });
            }
          }
          break;

        case 'ArrowRight':
          // Nudge position right
          if (selectedLayerIds.length === 1) {
            event.preventDefault();
            const layer = getLayerById(selectedLayerIds[0]);
            if (layer?.position.mode === 'absolute') {
              const delta = event.shiftKey ? 10 : 1;
              updateLayer(selectedLayerIds[0], {
                position: { mode: 'absolute', x: layer.position.x + delta, y: layer.position.y },
              });
            }
          }
          break;

        case '[':
          // Move layer back (Cmd+[)
          if (isMeta && selectedLayerIds.length === 1) {
            event.preventDefault();
            moveLayerDown(selectedLayerIds[0]);
          }
          break;

        case ']':
          // Move layer forward (Cmd+])
          if (isMeta && selectedLayerIds.length === 1) {
            event.preventDefault();
            moveLayerUp(selectedLayerIds[0]);
          }
          break;
      }
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      if (event.code === 'Space') {
        setIsSpaceHeld(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [
    enabled,
    selectedLayerIds,
    currentTemplate,
    deleteLayer,
    duplicateLayer,
    groupSelectedLayers,
    ungroupLayer,
    copySelectedLayers,
    cutSelectedLayers,
    pasteLayers,
    undo,
    redo,
    clearSelection,
    selectLayers,
    getLayerById,
    updateLayer,
    moveLayerUp,
    moveLayerDown,
    setActiveTool,
  ]);

  return {
    canvasRef,
    handleLayerClick,
    handleCanvasClick,
    handleLayerHover,
    handleMouseDown,
    handleCanvasMouseDown,
    handleContainerMouseDown,
    handleResizeStart,
    isDragging: dragState.isDragging,
    isPanning: panState.isPanning,
    isDrawing: shapeDrawState.isDrawing,
    isResizing: resizeState.isResizing,
    shapeDrawState,
    toolCursor,
    effectiveTool,
    guides,
    colorPickerState,
  };
}
